import { patchTech } from "../../../api/tech/patchTech";
import { patchVet } from "../../../api/vet/patchVet";
import { validateRequiredKeys } from "../../../Common/formUtils";
import { identifyPosthog } from "../../../Common/Wrappers/identifyPosthog";
import { USER_TYPE } from "../../../constants/UserTypeConstants";
import { Event, EventTracker } from "../../../tracking/service/EventTracker/EventTrackerService";
import { useRedirect } from "../Common/hooks/useRedirect";
import { RegistrationContextType, useRegistrationContext } from "../Common/registrationContext";
import { useIsEntity } from "../Common/service";
import { getIsLicenseRequired } from "../RegistrationLicense/service";

import { RegistrationLocationFormType } from "./RegistrationLocationFormContext";

export const validate = (form: RegistrationLocationFormType) => {
  const requiredKeys: (keyof RegistrationLocationFormType)[] = ["zipcode", "travelDistance"];
  const errors = validateRequiredKeys({ form, requiredKeys });
  if (!form.zipcode || form["zipcode"].length < 5) {
    errors["zipcode"] = "Please enter a valid ZIP code";
  }
  return errors;
};

export const sendTrackingEvent = (
  registrationContext: RegistrationContextType["registrationContext"]
) => {
  switch (registrationContext.userType) {
    case USER_TYPE.VET_USER_TYPE:
      EventTracker.send({
        eventName: Event.Name.VET_CLICK_ZIP,
        eventType: Event.Type.CLICK,
        entityType: Event.Entity.VET,
        entityId: registrationContext.entityId,
      });
      break;
    case USER_TYPE.TECH_USER_TYPE:
      EventTracker.send({
        eventName: Event.Name.TECH_CLICK_ZIP,
        eventType: Event.Type.CLICK,
        entityType: Event.Entity.TECH,
        entityId: registrationContext.entityId,
      });
      break;
    default:
      break;
  }
};

export const useSave = () => {
  const { registrationContext, setRegistrationContext } = useRegistrationContext();
  const { redirectToLicense } = useRedirect();
  const { isVet, isTech } = useIsEntity();

  const save = async (form: RegistrationLocationFormType): Promise<void> => {
    if (isTech) {
      const data = await patchTech({
        userId: registrationContext.userId,
        techId: registrationContext.entityId,
        ...form,
      });

      const { techRegistrationFlow, stateId, stateName, metroAreaId } = data.data;

      identifyPosthog({
        userId: registrationContext.userId,
        metroAreaId,
        propertiesForFlags: { metroAreaId },
      });

      setRegistrationContext({
        registrationContext: {
          ...registrationContext,
          ...form,
          techRegistrationFlow,
          stateId,
          stateName,
          metroAreaId,
          licenses: getIsLicenseRequired(techRegistrationFlow)
            ? [{ id: stateId, licenceNumber: "" }]
            : registrationContext.licenses,
        },
      });
    } else if (isVet) {
      const data = await patchVet({
        userId: registrationContext.userId,
        vetId: registrationContext.entityId,
        ...form,
      });

      const { stateId, stateName, metroAreaId } = data.data;

      identifyPosthog({
        userId: registrationContext.userId,
        metroAreaId,
        propertiesForFlags: { metroAreaId },
      });

      setRegistrationContext({
        registrationContext: {
          ...registrationContext,
          ...form,
          stateId,
          stateName,
          metroAreaId,
          licenses: [{ id: stateId, licenceNumber: "" }],
        },
      });
    }

    sendTrackingEvent(registrationContext);
    redirectToLicense();
  };

  return { save };
};
